import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, LogOut, HelpCircle } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { BrandMark } from "@/components/BrandMark";

/**
 * <OnboardingShell currentStage="bank">…</OnboardingShell>
 *
 * Editorial split that mirrors the new auth shell:
 *   ▸ Left rail: wordmark, eyebrow, headline, vertical stage rail
 *     (Application → Approved → KYC → Bank → Terms → Active),
 *     and a BrandMark in the footer.
 *   ▸ Right rail: page content. No card chrome.
 *
 * Stages support: "application" | "approved" | "kyc" | "bank" | "terms" | "active"
 * Pass the stage that is currently active. Earlier stages render as "done"
 * (filled brand-700 disc with checkmark), later stages render as "upcoming"
 * (hollow disc with neutral text).
 */
export const ONBOARDING_STAGES = [
  { id: "application", label: "Application",     hint: "Submitted" },
  { id: "approved",    label: "Approved",        hint: "Decision letter sent" },
  { id: "kyc",         label: "Identity (KYC)",  hint: "Veriff selfie + ID" },
  { id: "bank",        label: "Bank account",    hint: "Encrypted with KMS" },
  { id: "terms",       label: "Terms",           hint: "Plain-English summary" },
  { id: "active",      label: "Active member",   hint: "Reserve any open deal" },
];

export function OnboardingShell({ children, currentStage = "application", helper, footerNote }) {
  const idx = ONBOARDING_STAGES.findIndex((s) => s.id === currentStage);
  return (
    <div className="min-h-screen bg-surface grid lg:grid-cols-[480px_1fr]">
      {/* ── Left rail (editorial) ──────────────────────── */}
      {/* Top-aligned: wordmark + content sit at the top, brandmark+help is
          pinned to the bottom via mt-auto. Vertical rhythm matches the right
          rail so eyebrows on both sides line up. */}
      <aside className="relative hidden lg:flex flex-col bg-surface-alt border-r border-line px-12 pt-10 pb-10 overflow-hidden lg:sticky lg:top-0 lg:h-screen">
        <div className="absolute inset-0 bg-grid-faint pointer-events-none opacity-70" />
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(closest-side, rgba(15,118,110,0.08), transparent)" }} />

        {/* Top: wordmark */}
        <Link to="/" className="relative z-10 inline-flex"><Wordmark size="md" /></Link>

        {/* Top-aligned content (matches the right rail's pt-10 after its utility bar) */}
        <div className="relative z-10 mt-10">
          <div className="eyebrow mb-3 text-brand-700">Onboarding</div>
          <h2 className="font-brand text-[36px] leading-[1.1] text-ink font-bold tracking-[-0.02em]">
            A few last steps before you&rsquo;re live.
          </h2>
          <p className="mt-4 text-body-sm text-ink-muted leading-relaxed max-w-sm">
            Most members reach the dashboard within 48 hours of applying. Each step takes minutes, not hours.
          </p>

          {/* Vertical stage rail */}
          <ol className="mt-7 relative">
            <span aria-hidden className="absolute left-[15px] top-3 bottom-3 w-px bg-line" />
            {ONBOARDING_STAGES.map((s, i) => {
              const done = i < idx;
              const current = i === idx;
              return (
                <li key={s.id} className="relative flex items-start gap-4 pb-5 last:pb-0">
                  <span className={`relative z-10 size-8 rounded-full grid place-items-center shrink-0 transition-colors ${
                    done ? "bg-brand-700 text-white" :
                    current ? "bg-surface text-brand-700 border-2 border-brand-700 ring-4 ring-brand-50" :
                    "bg-surface text-ink-hint border border-line"
                  }`}>
                    {done
                      ? <CheckCircle2 className="size-4" strokeWidth={2.4} />
                      : <span className="text-caption font-bold tabular-nums">{i + 1}</span>}
                  </span>
                  <div className="flex-1 pt-1.5 min-w-0">
                    <div className={`text-body-sm font-semibold ${current ? "text-ink" : done ? "text-ink-muted" : "text-ink-hint"}`}>
                      {s.label}
                    </div>
                    <div className={`text-caption mt-0.5 ${current ? "text-brand-700" : "text-ink-hint"}`}>
                      {current ? "In progress" : done ? "Complete" : s.hint}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Bottom: brandmark + help (pinned via mt-auto) */}
        <div className="relative z-10 mt-auto flex items-center justify-between text-caption text-ink-hint pt-8">
          <BrandMark id="ONB" />
          <Link to="/faq" className="font-mono text-[10px] uppercase tracking-[0.18em] hover:text-ink inline-flex items-center gap-1.5">
            <HelpCircle className="size-3" /> Need help?
          </Link>
        </div>
      </aside>

      {/* ── Right rail (content, top-aligned) ──────────── */}
      <main className="relative flex flex-col min-h-screen">
        {/* Mobile compact header with progress chip */}
        <div className="lg:hidden border-b border-line px-6 py-5 flex items-center justify-between gap-3">
          <Link to="/"><Wordmark size="sm" /></Link>
          <span className="text-caption text-ink-subtle">
            Step <span className="text-ink font-semibold tabular-nums">{idx + 1}</span> of {ONBOARDING_STAGES.length} ·{" "}
            <span className="text-ink">{ONBOARDING_STAGES[idx]?.label}</span>
          </span>
        </div>

        {/* Top utility bar (Help · Sign out) — same row as the wordmark on the left rail */}
        <div className="hidden lg:flex border-b border-line px-12 h-[88px] items-center justify-end gap-1">
          <Link to="/faq" className="px-3 py-1.5 text-caption text-ink-muted hover:text-ink rounded-md inline-flex items-center gap-1.5">
            <HelpCircle className="size-3.5" /> Help
          </Link>
          <Link to="/login" className="px-3 py-1.5 text-caption text-ink-muted hover:text-ink rounded-md inline-flex items-center gap-1.5">
            <LogOut className="size-3.5" /> Sign out
          </Link>
        </div>

        {/* Content starts immediately under the utility bar. mt-10 on the
            page eyebrow inside lines up the right-rail eyebrow with the
            left-rail "Onboarding" eyebrow. */}
        <div className="flex-1 px-6 lg:px-12 pt-10 pb-12">
          <div className="max-w-3xl mx-auto">
            {children}
            {helper && <p className="mt-6 text-caption text-ink-subtle">{helper}</p>}
          </div>
        </div>

        {footerNote && (
          <div className="px-6 lg:px-12 pb-6 text-caption text-ink-hint">{footerNote}</div>
        )}
      </main>
    </div>
  );
}

export default OnboardingShell;
